(() => {
  'use strict';
  if (window.SlabHubEnergyStrip) return;
  // Phones/tablets and low-memory devices: static sphere images only, no WebGL.
  const lite = () => {
    const mem = navigator.deviceMemory;
    const saveData = navigator.connection && navigator.connection.saveData;
    return window.matchMedia('(max-width: 989px), (pointer: coarse)').matches || (mem && mem <= 4) || saveData;
  };
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strips = new Map();
  let viewerReady;
  const ensureViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (viewerReady) return viewerReady;
    viewerReady = new Promise((resolve, reject) => {
      let featureTimer;
      const deadline = setTimeout(() => reject(new Error('Energy viewer could not load')), 30000);
      const done = () => { clearTimeout(featureTimer); clearTimeout(deadline); resolve(); };
      customElements.whenDefined('model-viewer').then(done);
      const fallback = () => {
        clearTimeout(featureTimer);
        if (customElements.get('model-viewer')) { done(); return; }
        const existing = [...document.scripts].find(s => /(?:^|\/)model-viewer(?:\.min)?\.js(?:[?#]|$)/.test(s.src));
        if (existing) return;
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
        script.addEventListener('error', () => { clearTimeout(deadline); reject(new Error('Energy viewer library unavailable')); }, { once: true });
        document.head.append(script);
      };
      if (typeof window.Shopify?.loadFeatures === 'function') {
        featureTimer = setTimeout(fallback, 8000);
        try {
          window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {
            if (customElements.get('model-viewer')) done(); else fallback();
          } }]);
        } catch { fallback(); }
      } else fallback();
    }).catch(error => { viewerReady = null; throw error; });
    return viewerReady;
  };
  const spin = state => state.models.forEach(m =>
    m.toggleAttribute('auto-rotate', state.visible && m.loaded && !document.hidden && !reduced.matches));
  // Load one model at a time so the GPU never decodes all spheres at once.
  const loadNext = state => {
    if (state.loading || !state.visible) return;
    const next = state.models.find(m => !m.hasAttribute('src'));
    if (!next) return;
    state.loading = true;
    const finish = ok => {
      state.loading = false;
      if (ok) { next.closest('.sh-energy-strip__media')?.classList.add('is-3d'); next.resetTurntableRotation?.(); }
      spin(state);
      setTimeout(() => loadNext(state), 120);
    };
    next.addEventListener('load', () => finish(true), { once: true });
    next.addEventListener('error', () => finish(false), { once: true });
    next.setAttribute('src', next.dataset.energySrc);
  };
  const update = (strip, visible) => {
    const state = strips.get(strip);
    if (!state) return;
    state.visible = visible;
    if (visible && !state.ready) {
      if (!state.pending) {
        state.pending = ensureViewer().then(() => {
          state.ready = true; state.pending = null;
          update(strip, state.visible);
        }).catch(() => { state.pending = null; });
      }
      return;
    }
    spin(state);
    loadNext(state);
  };
  const init = (root = document) => {
    if (lite()) return;
    root.querySelectorAll('[data-sh-energy-strip]').forEach(strip => {
      if (strips.has(strip)) return;
      strip.querySelectorAll('template[data-energy-tpl]').forEach(t => t.replaceWith(t.content.cloneNode(true)));
      const state = { visible: false, ready: !!customElements.get('model-viewer'), pending: null, loading: false,
        models: [...strip.querySelectorAll('[data-energy-src]')], observer: null };
      strips.set(strip, state);
      state.observer = new IntersectionObserver(entries => entries.forEach(e => update(strip, e.isIntersecting)), { rootMargin: '200px 0px', threshold: 0 });
      state.observer.observe(strip);
    });
  };
  const refresh = () => strips.forEach((state, strip) => update(strip, state.visible));
  reduced.addEventListener('change', refresh);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => {
    strips.forEach((state, strip) => { if (e.target.contains(strip)) { state.observer.disconnect(); strips.delete(strip); } });
  });
  window.SlabHubEnergyStrip = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
